import { FC, useMemo, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form, Select } from 'antd';
import styled from 'styled-components';

import { Input } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useCurrencies } from '@/shared/hooks/use-currencies';

import { InterwillAssetFormParams } from './types';

type InterwillAssetFormValueFieldProps = {
  control: Control<InterwillAssetFormParams, unknown>;
};

export const InterwillAssetFormValueField: FC<InterwillAssetFormValueFieldProps> = ({
  control,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addEntity.asset.fields' });
  const { list } = useCurrencies();
  const [hasError, setHasError] = useState(false);

  const sortedList = useMemo(
    () => [...list].sort((a, b) => a.currencyCode.localeCompare(b.currencyCode)),
    [list],
  );

  const inputStyle = useMemo(() => {
    const color = hasError ? COLORS.error : COLORS.colorBorder;
    const border = `1px solid ${color}`;

    return {
      border,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    };
  }, [hasError]);

  return (
    <>
      <Container>
        <Controller
          name={'value'}
          control={control}
          rules={{ required: t('value.error') }}
          render={({ field, fieldState }) => {
            if (fieldState.error) {
              setHasError(true);
            } else {
              setHasError(false);
            }

            return (
              <Form.Item
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
                style={{
                  flex: 1,
                }}
              >
                <Input
                  variant="borderless"
                  placeholder={t(`value.placeholder`)}
                  type={'number'}
                  style={inputStyle}
                  {...field}
                />
              </Form.Item>
            );
          }}
        />

        <Controller
          name={'currency'}
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => {
            return (
              <Form.Item
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <StyledSelect
                  variant="borderless"
                  placeholder={'Currency'}
                  options={sortedList.map((item) => ({
                    key: item.currencyCode,
                    label: item.currencyCode,
                    value: item.currencyCode,
                  }))}
                  $hasError={hasError}
                  {...field}
                />
              </Form.Item>
            );
          }}
        />
      </Container>
    </>
  );
};

const Container = styled.div`
  display: flex;
`;

const StyledSelect = styled(Select)<{ $hasError: boolean }>`
  display: flex;

  border: 1px solid ${({ $hasError }) => ($hasError ? COLORS.error : COLORS.colorBorder)};
  border-left: none;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  transition: all 0.2s;
`;
